/**
 * TrouveRecommendationChip — one-tap "Save to Arsenal" chip
 *
 * Renders as a compact pill below an assistant message whenever
 * Trouvé recommended a specific catalog product. Click → POSTs to
 * /trouve/recommendations/save (which idempotently writes to the
 * user's Beauty Arsenal) and locks into a "Saved ✓" state.
 *
 * The chip closes the gap between Trouvé's voice ("try Rare Beauty
 * Soft Pinch Liquid Blush") and the user's actual product log — no
 * copy-paste, no separate Arsenal page visit, no friction.
 */
import React, { useState } from "react";
import { Bookmark, BookmarkCheck, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { saveTrouveRecommendation } from "../lib/api";
import { track } from "../lib/analytics";

export const TrouveRecommendationChip = ({
  recommendation,
  messageId,
  sessionId,
  initiallySaved = false,
  dataTestidPrefix = "trouve-rec-chip",
}) => {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(initiallySaved);

  const handleSave = async () => {
    if (saving || saved) return;
    setSaving(true);
    try {
      const res = await saveTrouveRecommendation({
        brand: recommendation.brand,
        name: recommendation.name,
        category: recommendation.category,
        messageId,
        sessionId,
      });
      setSaved(true);
      track("trouve_chat_recommendation_saved", {
        brand: recommendation.brand,
        source: recommendation.source,
        already_saved: !!res.already_saved,
      });
      toast.success(
        res.already_saved
          ? t("trouveChat.alreadyInArsenal")
          : t("trouveChat.savedToArsenal"),
      );
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      if (typeof detail === "string" && detail.startsWith("arsenal_full")) {
        toast.error(t("trouveChat.arsenalFull"));
      } else {
        toast.error(t("trouveChat.saveErrorGeneric"));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleSave}
      disabled={saving || saved}
      data-testid={`${dataTestidPrefix}-${recommendation.product_id || recommendation.brand}`}
      data-product-id={recommendation.product_id || ""}
      data-saved={saved ? "true" : "false"}
      className={`inline-flex items-center gap-1.5 max-w-full text-left text-[11px] px-2.5 py-1.5 rounded-full border transition group ${
        saved
          ? "bg-[#FBE7EE] border-[#9B6A6C]/40 text-[#9B6A6C] cursor-default"
          : "bg-white border-[#EADDD7] text-[#2C2123] hover:border-[#9B6A6C]/40 hover:bg-[#FDFBF7] disabled:opacity-60"
      }`}
    >
      {saving ? (
        <Loader2 className="h-3 w-3 animate-spin shrink-0" />
      ) : saved ? (
        <BookmarkCheck className="h-3 w-3 shrink-0 text-[#9B6A6C]" />
      ) : (
        <Bookmark className="h-3 w-3 shrink-0 text-[#9B6A6C]" />
      )}
      <span className="truncate font-medium">
        {recommendation.brand}
      </span>
      <span className="text-[#8A7D82] truncate hidden sm:inline">
        · {recommendation.name}
      </span>
      <span className="text-[10px] uppercase tracking-wider text-[#9B6A6C] pl-1 shrink-0">
        {saved ? t("trouveChat.savedToArsenal") : t("trouveChat.saveToArsenal")}
      </span>
    </button>
  );
};

export default TrouveRecommendationChip;
